import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import _noop from 'lodash/noop';

import COLORS from '@mlb/react-patterns/style/universal.color';
import InningStatePattern from '@mlb/react-patterns/components/InningState/inningState.tsx';
import {
    StyledCountWrapper,
    StyledInningHalf,
    StyledBasepathsWrapper,
} from '@mlb/react-patterns/components/InningState/inningState.style';
import { mainCq } from 'bdvp-common/src/styles/helpers/main-cq';

import PlayerInfo, { Wrapper as PlayerInfoWrapper } from './PlayerInfo';
import Stats from './Stats';
import { useDictionary } from '../../../hooks/config';
import { SEARCH_ROUTE } from '../../../utils/constants';

const Section = styled.section`
    border-top: 1px solid ${COLORS.mlbGrey7};
    color: ${COLORS.mlbGrey3};
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 10px 0;
    border-bottom: 1px solid ${COLORS.mlbGrey7};
    margin-top: 10px;
   ${mainCq.md`
        margin-top: 20px;
    `}
   ${mainCq.xl`
        flex-direction: row;
    `}
`;

const PlayersWrapper = styled.div`
    display: flex;
    margin: 10px 0;
    flex-direction: column;
    flex-basis: 84%;
   ${mainCq.md`
        flex-direction: row;
        margin: 25px 0;
    `}
   ${mainCq.xl`
        padding-right: 5px;
    `}

    ${PlayerInfoWrapper} {
        &:first-of-type {
            margin-bottom: 30px;
        }
       ${mainCq.md`
            &:first-of-type {
                margin-bottom: 0;
            }
        `}
        &:last-of-type {
            justify-content: flex-start;
           ${mainCq.md`
                justify-content: flex-end;
            `}
           ${mainCq.xl`
                justify-content: flex-start;
            `}
        }
    }
`;

const InningStateWrapper = styled.div`
    align-items: center;
    border-left: none;
    border-top: 1px solid ${COLORS.mlbGrey7};
    display: flex;
    justify-content: center;
    margin-top: 5px;
    padding-top: 10px;

   ${mainCq.xl`
        border-left: 1px solid ${COLORS.mlbGrey7};
        border-top: none;
        flex-basis: 16%;
        margin: 20px 0;
        padding-top: 0;
    `}
`;

const InningState = styled(InningStatePattern)`

    .main-cq--XS:not(.main-cq--XL) & {
        align-items: center;
        display: flex;
        width: auto;
        ${StyledInningHalf} {
            font-size: 11px;
            margin-bottom: 0;
            margin-left: auto;
            width: 100%;
        }
        ${StyledCountWrapper} {
            font-size: 11px;
            margin-left: auto;
            margin-top: 0;
            width: 100%;
        }
        ${StyledBasepathsWrapper} {
            margin: 0 5px 0 0;
        }
    }
`;

const pitcherStats = [];
const batterStats = [];

const PlayerMatchup = ({
    mediaPlayback,
    searchRoute,
    onPlayerClick,
    className,
}) => {

    const {
        pitch_type = 'Pitch Type',
        pitch_speed = 'Pitch Speed',
        spin_rate = 'Spin Rate',
        exit_velocity = 'Exit Velocity',
        launch_angle = 'Launch Angle',
        hit_distance = 'Hit Distance',
        pitching: pitching_text = 'Pitching',
        batting: batting_text = 'Batting',
    } = useDictionary();

    const { playInfo } = mediaPlayback;

    // If we're missing both player's information, we should return null.
    if(!playInfo || !playInfo.isSinglePlay) {

        return null;

    }

    const { pitcher, batter } = playInfo.players;
    const { pitching, batting } = playInfo.teams;

    const {
        balls,
        strikes,
        inning,
        inningHalf,
        outs,
        pitchType,
        pitchSpeed,
        spinRate,
        exitVelocity,
        launchAngle,
        hitDistance,
        runners,
    } = playInfo;

    pitcherStats.splice(0, pitcherStats.length, ...[
        {
            value: pitchType,
            label: pitch_type,
        },
        {
            value: pitchSpeed,
            label: pitch_speed,
        },
        {
            value: spinRate,
            label: spin_rate,
        },
    ]);

    batterStats.splice(0, batterStats.length, ...[
        {
            value: exitVelocity,
            label: exit_velocity,
        },
        {
            value: launchAngle,
            label: launch_angle,
        },
        {
            value: hitDistance,
            label: hit_distance,
        },
    ]);

    return (
        <Section className={className}>
            <PlayersWrapper>
                <PlayerInfo
                    player={pitcher}
                    teamTriCode={pitching.triCode}
                    label={pitching_text}
                    searchRoute={searchRoute}
                    onPlayerClick={onPlayerClick}
                    stats={(<Stats data={pitcherStats} />)}
                />
                <PlayerInfo
                    player={batter}
                    teamTriCode={batting.triCode}
                    label={batting_text}
                    searchRoute={searchRoute}
                    onPlayerClick={onPlayerClick}
                    stats={(<Stats data={batterStats} />)}
                />
            </PlayersWrapper>
            {(inningHalf && inning) && (
                <InningStateWrapper>
                    <InningState
                        outCount={outs}
                        balls={balls}
                        strikes={strikes}
                        first={runners.first}
                        second={runners.second}
                        third={runners.third}
                        showOutsText={true}
                        inningHalfText={`${inningHalf.toUpperCase()} ${inning}`}
                        outsWidth={65}
                        basepathsWidth={24}
                    />
                </InningStateWrapper>
            )}
        </Section>
    );

};

PlayerMatchup.propTypes = {
    mediaPlayback: PropTypes.object,
    searchRoute: PropTypes.string,
    onPlayerClick: PropTypes.func,
    className: PropTypes.string,
};

PlayerMatchup.defaultProps = {
    mediaPlayback: {},
    searchRoute: SEARCH_ROUTE,
    onPlayerClick: _noop,
    className: undefined,
};

export default PlayerMatchup;
